import * as Sentry from "@sentry/nextjs";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";

import { useErrorToaster } from "src/common/components/ErrorDetails";
import { client, endpoint, handleAllPromises } from "src/lib/bridgeClient";
import { SERVER_COMM_ERROR } from "src/lib/error";
import { pluralize } from "src/lib/grammar";

export const deleteDisk = async (client: Client, id: string) => {
  return await client(endpoint.deleteMyDisk(id), "DELETE");
};

export const useDeleteDisk = () => {
  const { mutate } = useSWRConfig();
  const toastError = useErrorToaster();

  return async (diskIds: Array<string>): Promise<PromiseSettledResult<AxiosResponse>[]> => {
    let responses: PromiseSettledResult<AxiosResponse>[];

    try {
      const promises = [];
      diskIds.forEach((id) => {
        promises.push(deleteDisk(client, id));
      });

      // Make all requests async and wait for all them to resolve
      responses = await Promise.allSettled(promises);
      const { successes, failures } = handleAllPromises(responses);

      const apiErrors: string[] = [];
      for (const failure of failures) {
        if (failure.response.data?.error) {
          apiErrors.push(failure.response.data?.error);
        }
      }

      const numErrors = apiErrors.length;
      if (numErrors > 0) {
        toastError(`Failed to delete ${numErrors} ${pluralize("disk", numErrors)}`, apiErrors);
      }

      const numSuccess = successes.length;
      if (numSuccess > 0) {
        mutate(endpoint.getMyDisks);
        mutate(endpoint.getMyOrgDisks);
        toast.success(`${numSuccess} ${pluralize("disk", numSuccess)} deleted`);
      }
    } catch (error) {
      toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
      Sentry.captureException(error);

      return error.response;
    }

    return responses;
  };
};
